import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { QuotationRow } from '../../types';

// Void a quotation (Calem, Bugify #1adffffd): the Quotations list's ⋮ Void asks why, from Calem's list, Other
// opening a box for the words, and the server records it (POST /api/quotations/:id/void). The codes are the
// server's (services/quotationVoid.ts VOID_REASONS, plus 'other'); the words shown are each language's.
const VOID_CODES = ['no_reply', 'other_firm', 'not_proceeding', 'superseded', 'other'];

/** A voided quotation's reason in the reader's language — the English the server kept for a code it doesn't know. */
export function voidReasonLabel(t: any, v: NonNullable<QuotationRow['voided']>): string {
  if (v.code === 'other') return `${t('qo_void_r_other')} — ${v.note}`;
  return VOID_CODES.includes(v.code) ? t(`qo_void_r_${v.code}`) : v.reason;
}

export default function QuotationVoidDialog({ id, docNo, onClose, onDone }: { id: string; docNo: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ready = !!code && (code !== 'other' || !!note.trim());
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await api.voidQuotation(id, code, code === 'other' ? note.trim() : undefined); onDone(); }
    catch (e: any) { setErr(e?.message || 'Failed'); setBusy(false); }
  };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div style={{ width: 440, maxWidth: '92vw', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('qo_void_title', { no: docNo })}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3, marginBottom: 16, lineHeight: 1.5 }}>{t('qo_void_hint')}</div>
        <label style={lbl}>{t('qo_void_reason')}</label>
        <select value={code} onChange={e => setCode(e.target.value)} autoFocus style={inp}>
          <option value="" disabled>{t('qo_void_pick')}</option>
          {VOID_CODES.map(c => <option key={c} value={c}>{t(`qo_void_r_${c}`)}</option>)}
        </select>
        {code === 'other' && (
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={300} autoFocus placeholder={t('qo_void_other_ph')}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inp, marginTop: 8 }} />
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={submit} disabled={!ready || busy} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#B42318', color: '#fff', fontWeight: 700, fontSize: 13, cursor: ready && !busy ? 'pointer' : 'default', opacity: ready && !busy ? 1 : 0.5 }}>{busy ? t('login_wait') : t('qo_void')}</button>
        </div>
      </div>
    </div>
  );
}
